import Select from 'react-select';

export function FormRow({ row, index, brands, stores, products, onUpdate, onRemove, canRemove }) {
  // Get unique region options for the selected brand
  const getRegionOptions = () => {
    if (!row.brand || !stores[row.brand]) return [];
    const regions = stores[row.brand].map(store => store.regionName);
    const uniqueRegions = [...new Set(regions)];
    return uniqueRegions.sort();
  };

  // Get store options for the selected brand and region
  const getStoreOptions = () => {
    if (!row.brand || !row.regionName || !stores[row.brand]) return [];
    return stores[row.brand]
      .filter(store => store.regionName === row.regionName)
      .map(store => ({
        value: store.storeName,
        label: store.storeName,
        regionName: store.regionName
      }));
  };

  // Handle store name selection
  const handleStoreNameChange = (selectedOption) => {
    const storeName = selectedOption ? selectedOption.value : '';
    onUpdate(row.id, 'storeName', storeName);
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Row {index + 1}</h3>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(row.id)}
            className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors"
            title="Remove row"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
        {/* Brand Dropdown */}
        <div className="lg:col-span-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Brand *
          </label>
          <select
            value={row.brand}
            onChange={(e) => onUpdate(row.id, 'brand', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Select Brand</option>
            {Object.keys(brands).map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </div>

        {/* Region Dropdown */}
        <div className="lg:col-span-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Region *
          </label>
          <select
            value={row.regionName}
            onChange={(e) => onUpdate(row.id, 'regionName', e.target.value)}
            disabled={!row.brand}
            className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
          >
            <option value="">Select Region</option>
            {row.brand && getRegionOptions().map((regionName) => (
              <option key={regionName} value={regionName}>
                {regionName}
              </option>
            ))}
          </select>
        </div>

        {/* Store Name (Searchable Dropdown) - Larger field */}
        <div className="lg:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Store Name *
          </label>
          <Select
            value={row.storeName ? { value: row.storeName, label: row.storeName } : null}
            onChange={handleStoreNameChange}
            options={getStoreOptions()}
            placeholder="Type or select store..."
            isSearchable={true}
            isClearable={true}
            isDisabled={!row.brand || !row.regionName}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (provided, state) => ({
                ...provided,
                minHeight: '38px',
                fontSize: '14px',
                borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
                boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
                '&:hover': {
                  borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB'
                }
              }),
              placeholder: (provided) => ({
                ...provided,
                color: '#9CA3AF'
              }),
              singleValue: (provided) => ({
                ...provided,
                color: '#374151'
              })
            }}
          />
        </div>

        {/* Product Dropdown */}
        <div className="lg:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Product *
          </label>
          <select
            value={row.product}
            onChange={(e) => onUpdate(row.id, 'product', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Select Product</option>
            {products.map((product) => (
              <option key={product.name} value={product.name}>
                {product.name}
              </option>
            ))}
          </select>
        </div>

        {/* Credit Cost (Auto-populated) */}
        <div className="lg:col-span-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Credit Cost
          </label>
          <input
            type="number"
            value={row.creditCost}
            readOnly
            placeholder="Auto-populated"
            className="w-full p-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-600"
          />
        </div>

        {/* Wage Input */}
        <div className="lg:col-span-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Wage
          </label>
          <input
            type="text"
            value={row.wage}
            onChange={(e) => onUpdate(row.id, 'wage', e.target.value)}
            placeholder="17.50-20.00"          
            className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Notes Input */}
        <div className="lg:col-span-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Notes
          </label>
          <input
            type="text"
            value={row.notes}
            onChange={(e) => onUpdate(row.id, 'notes', e.target.value)}
            placeholder="Optional notes"
            className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
    </div>
  );
}
